import React, { useState, useEffect } from 'react';
import { TextBoxComponent } from '@syncfusion/ej2-react-inputs';
import { eyeIcon, eyeSlashIcon } from 'assets';
import clsx from 'clsx';
import './OpPasswordBox.css';
import { getPasswordStrength } from './opPasswordBox.helper';

/**
 * OpPasswordBox - A wrapper around Syncfusion TextBoxComponent for password entry.
 *
 * Provides a standardized password input with support for:
 * - Password visibility toggle (show / hide password)
 * - Label rendering (top or inside positioning)
 * - Required indicator (asterisk)
 * - Error message display
 * - Disabled state styling
 * - Optional password strength validation UI
 *
 * Features:
 * - Built on Syncfusion `TextBoxComponent`
 * - Manages its own internal state, meaning the parent does not need to feed the value back
 * - `value` acts as an initial preload
 * - `onChange` notifies the parent of changes
 * - Optional password strength meter that evaluates:
 *   - Minimum length of 8 characters
 *   - At least one uppercase letter
 *   - At least one number
 *   - At least one special character
 *
 * Password Strength Levels:
 * - 0: Very Weak
 * - 1: Weak
 * - 2: Medium
 * - 3: Strong
 * - 4: Very Strong
 *
 * Props:
 *
 * @param {boolean} [enabled=true]
 * Controls whether the input is enabled or disabled.
 *
 * @param {boolean} [enableStrongPasswordValidation=false]
 * Displays a password strength indicator below the input.
 * Does not automatically prevent submission; consumers should
 * implement validation rules as needed.
 *
 * @param {string} [errorMessage]
 * Displays an error message below the input and applies error styling.
 *
 * @param {string} label
 * Text label displayed above or inside the input depending on `labelPosition`.
 *
 * @param {'Top'|'Inside'} [labelPosition='Top']
 * Determines label placement relative to the input field.
 *
 * @param {boolean} [showAsterisk]
 * If true, displays a required field asterisk (*) next to the label.
 *
 * @param {string} [value]
 * Initial preload value of the password field.
 *
 * @param {function} [onChange]
 * Callback fired when the password changes.
 * Receives the updated password string.
 *
 * @param {object} textBoxParams
 * Additional props passed directly to Syncfusion TextBoxComponent.
 */
export function OpPasswordBox({
  enabled = true,
  enableStrongPasswordValidation = false,
  errorMessage,
  label,
  labelPosition = 'Top',
  showAsterisk,
  value,
  onChange,
  ...textBoxParams
}) {
  const asterisk = showAsterisk ? '*' : '';
  const isDisabled = !enabled;
  const isLabelInside = labelPosition === 'Inside';

  const [internalValue, setInternalValue] = useState(value ?? '');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const strength = getPasswordStrength(internalValue);

  const strengthLabel = [
    'Very Weak',
    'Weak',
    'Medium',
    'Strong',
    'Very Strong',
  ][strength];

  const handleInput = (args) => {
    const currentValue = args.value ?? '';

    setInternalValue(currentValue);

    onChange?.(currentValue);

    textBoxParams.input?.(args);
  };

  return (
    <div
      className={clsx(
        'op-password-wrapper',
        isLabelInside && 'op-password-wrapper-label-inside',
        isDisabled && 'op-password-wrapper-disabled',
        isDisabled &&
          isLabelInside &&
          'op-password-wrapper-disabled-label-inside',
      )}
    >
      {label && (
        <label
          className={clsx(
            'op-password-label',
            isLabelInside && 'op-password-label-inside',
          )}
        >
          {label}
          <span className="op-password-asterisk">{asterisk}</span>
        </label>
      )}

      <div className="op-password-input-container">
        <TextBoxComponent
          {...textBoxParams}
          className={clsx(
            'op-password-input',
            errorMessage && 'op-password-error-input',
          )}
          enabled={enabled}
          input={handleInput}
          type={showPassword ? 'text' : 'password'}
          value={internalValue}
        />

        <button
          className="op-password-eye-button"
          onClick={() => setShowPassword((prev) => !prev)}
          tabIndex={-1}
          type="button"
        >
          <img
            alt={showPassword ? 'Hide Password' : 'Show Password'}
            className="op-password-eye-icon"
            src={showPassword ? eyeSlashIcon : eyeIcon}
            style={{ marginTop: showPassword && '-2px' }}
          />
        </button>
      </div>

      {enableStrongPasswordValidation && (
        <div className="op-password-strength-container">
          <div className="op-password-strength-bars">
            {[1, 2, 3, 4].map((bar) => (
              <div
                key={bar}
                className={clsx(
                  'op-password-strength-bar',
                  strength >= bar && `strength-${strength}`,
                )}
              />
            ))}
          </div>

          <span className="op-password-strength-label">{strengthLabel}</span>

          {/* Hide for now but good for UI display */}
          {/* <ul className="op-password-rules">
            <li className={clsx(internalValue?.length >= 8 && 'passed')}>
              At least 8 characters
            </li>

            <li className={clsx(/[A-Z]/.test(internalValue) && 'passed')}>
              One uppercase letter
            </li>

            <li className={clsx(/[0-9]/.test(internalValue) && 'passed')}>
              One number
            </li>

            <li
              className={clsx(/[^A-Za-z0-9]/.test(internalValue) && 'passed')}
            >
              One special character
            </li>
          </ul> */}
        </div>
      )}

      {errorMessage && (
        <span className="op-password-error-message">{errorMessage}</span>
      )}
    </div>
  );
}
